/**
 * activityConfig - Configuração compartilhada de tipos de atividade CRM
 *
 * Fonte única para ícone, cores, placeholders e flags de UI dos tipos
 * de atividade CRM (call | meeting | email | task | note). Consumido por:
 *  - ActivityFormSheet (sheet lateral)
 *  - DealInlineActivityCreator (criador inline na timeline)
 *
 * @version 1.0.0
 * RELEASE: 2026-04-07 v1.0.0 - Extração para reuso entre Sheet e criador inline
 */
import { Phone, Calendar, Mail, CheckSquare, FileText } from 'lucide-react';
import type { QuickActivityType } from './QuickActivityBar';

/** Tipos de atividade que possuem formulário (exclui ações especiais) */
export type CrmFormActivityType = Exclude<QuickActivityType, 'scheduling' | 'contracts'>;

/** Configuração visual/funcional por tipo de atividade */
export interface CrmActivityConfig {
  icon: typeof Phone;
  title: string;
  titlePlaceholder: string;
  /** Classe de cor do ícone/label */
  color: string;
  /** Classe de background do badge do ícone */
  bgClass: string;
  /** Exibe seletor de data/hora */
  showSchedule: boolean;
  /** Usa RichTextEditor para descrição */
  showRichText: boolean;
  /** Permite anexos */
  showAttachments: boolean;
}

/**
 * Mapa único de configuração por tipo de atividade
 */
export const CRM_ACTIVITY_CONFIG: Record<CrmFormActivityType, CrmActivityConfig> = {
  call: {
    icon: Phone,
    title: 'Nova Ligação',
    titlePlaceholder: 'Ex: Ligação de follow-up',
    color: 'text-emerald-600 dark:text-emerald-400',
    bgClass: 'bg-emerald-100 dark:bg-emerald-900/30',
    showSchedule: false,
    showRichText: false,
    showAttachments: false,
  },
  meeting: {
    icon: Calendar,
    title: 'Nova Reunião',
    titlePlaceholder: 'Ex: Reunião de apresentação',
    color: 'text-orange-600 dark:text-orange-400',
    bgClass: 'bg-orange-100 dark:bg-orange-900/30',
    showSchedule: true,
    showRichText: false,
    showAttachments: false,
  },
  email: {
    icon: Mail,
    title: 'Novo E-mail',
    titlePlaceholder: 'Ex: Envio de proposta',
    color: 'text-violet-600 dark:text-violet-400',
    bgClass: 'bg-violet-100 dark:bg-violet-900/30',
    showSchedule: false,
    showRichText: false,
    showAttachments: false,
  },
  task: {
    icon: CheckSquare,
    title: 'Novo Lembrete',
    titlePlaceholder: 'Ex: Preparar documentação',
    color: 'text-amber-600 dark:text-amber-400',
    bgClass: 'bg-amber-100 dark:bg-amber-900/30',
    showSchedule: true,
    showRichText: false,
    showAttachments: false,
  },
  note: {
    icon: FileText,
    title: 'Nova Nota',
    titlePlaceholder: 'Ex: Resumo da conversa',
    color: 'text-blue-600 dark:text-blue-400',
    bgClass: 'bg-blue-100 dark:bg-blue-900/30',
    showSchedule: false,
    showRichText: true,
    showAttachments: true,
  },
};
